'use client'

import { useEffect, useState } from "react"
import { Bell, X } from "lucide-react"
import { getUnresolvedNotifications, resolveNotification, Notification } from "@/lib/supabase/queries/notifications-supabase-query"
import { toast } from "sonner"

export function AdminNotificationBar() {
    const [notifications, setNotifications] = useState<Notification[]>([])
    const [isOpen, setIsOpen] = useState(false)
    
    useEffect(() => {
        const fetchNotifications = async () => {
            try {
                const notifs = await getUnresolvedNotifications()
                setNotifications(notifs)
            } catch (error) {
                console.error("Failed to fetch notifications:", error)
            }
        }
        
        fetchNotifications()
        
        // Optional: Set interval for polling
        const interval = setInterval(fetchNotifications, 30000)
        return () => clearInterval(interval)
    }, [])
    
    const handleResolve = async (id: string) => {
        try {
            // Optimistically update UI first
            setNotifications(notifications.filter(n => n.id !== id))
            
            // Then perform the actual API call
            const success = await resolveNotification(id)
            
            // If the API call fails, restore the notification
            if (!success) {
                const notifs = await getUnresolvedNotifications()
                setNotifications(notifs)
                toast.error("Failed to dismiss notification")
            }
        } catch (error) {
            console.error("Failed to resolve notification:", error)
            toast.error("Failed to dismiss notification")
            
            // Refresh notifications to ensure UI is in sync with server
            const notifs = await getUnresolvedNotifications()
            setNotifications(notifs)
        }
    }
    
    if (notifications.length === 0) return null
    
    return (
        <div className={`fixed top-0 left-0 w-full max-h-4 ${notifications.length > 0 ? 'bg-destructive' : 'bg-background4'} text-foreground px-4 py-1 flex justify-between items-center z-50 text-xs`}>
            <div>Admin Notifications</div>
            <div className="flex items-center">
                <button 
                    onClick={() => setIsOpen(!isOpen)} 
                    className="relative flex items-center gap-2 text-xs"
                >
                    <Bell className="size-3" />
                    <span>Notifications</span>
                    <span className="bg-primary text-primary-foreground rounded-full w-4 h-4 flex items-center justify-center text-xs">
                        {notifications.length}
                    </span>
                </button>
                
                {isOpen && (
                    <div className="absolute top-6 right-4 bg-background4 shadow-lg rounded-md py-2 z-50 text-foreground max-h-[80vh] overflow-y-auto text-xs">
                        <div className="flex justify-between items-center px-4 pb-2 border-b">
                            <h3 className="font-medium text-xs">Notifications</h3>
                            <button onClick={() => setIsOpen(false)}>
                                <X className="size-3" />
                            </button>
                        </div>
                        <div className="py-2">
                            {notifications.map((notification) => (
                                <div key={notification.id} className="px-4 py-2 border-b last:border-b-0 hover:bg-muted">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <p className="text-xs">{notification.message}</p>
                                            <p className="text-xs text-muted-foreground">
                                                {new Date(notification.created_at).toLocaleString()}
                                            </p>
                                        </div>
                                        <button 
                                            onClick={() => handleResolve(notification.id)}
                                            className="text-xs px-2 py-1 bg-muted rounded hover:bg-accent"
                                        >
                                            Dismiss
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
} 